import React, { useCallback, useMemo, useState } from 'react';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { InternalApiAlertNoteTemplate, InternalApiAlertType, InternalApiSortDirection } from '../../api-internal';
import {
  SalvButton,
  SalvRadioToggleButton,
  SalvRadioToggleGroup,
  SalvTable,
  SalvTablePaginationType,
  SalvTextField,
  Size,
} from '@salvapp/ui-components';
import { faMagnifyingGlass, faPlus } from '@fortawesome/pro-solid-svg-icons';
import AlertNoteTemplateEditor from './AlertNoteTemplateEditor';
import { alertNoteTemplateInternalApi } from '../../apis';
import SalvPage from '../../common/SalvPage';
import { createColumnHelper, SortingState, Updater } from '@tanstack/react-table';
import './AlertNoteTemplateList.scss';
import dayjs from 'dayjs';
import { FULL_DATE_TIME_FORMAT } from '../../common/helper';
import { useSearchParams } from 'react-router';
import { getConfigurationUrl } from '../../navigation/helper';
import { ConfigurationProduct } from '../../settings/configuration/Configuration';
import Has from '../../auth/Has';
import { CAN_ADD_ALERT_NOTE_TEMPLATE } from '../../auth/permissions';
import { FindAlertNoteTemplatesOrderByEnum } from '../../api-internal';
import { isFeatureEnabled, ORGANISATION_FEATURE } from '../../organisation/feature/helper';
import { useMe } from '../../organisation/useMe';
import { getOrderByAndDirection } from '../../common/sort';

interface AlertNoteTemplateListProps {
  alertType?: InternalApiAlertType;
}

const columnHelper = createColumnHelper<InternalApiAlertNoteTemplate>();

const AlertNoteTemplateList: React.FC<AlertNoteTemplateListProps> = ({ alertType: _alertType }) => {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const { features } = useMe();

  const isBridgeEnabled = isFeatureEnabled(ORGANISATION_FEATURE.BRIDGE, features);
  const [showEditor, setShowEditor] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<InternalApiAlertNoteTemplate | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [size] = useState(20);

  const selectedAlertType = (searchParams.get('alertType') as InternalApiAlertType | 'ALL') || 'ALL';

  const sortByParam = searchParams.get('sortBy') || 'UPDATED_TIME';
  const sortDirectionParam = searchParams.get('sortDirection') || 'DESC';
  const sortBy =
    sortByParam === 'NAME' ? FindAlertNoteTemplatesOrderByEnum.NAME : FindAlertNoteTemplatesOrderByEnum.UPDATED_TIME;
  const sortDirection = sortDirectionParam === 'ASC' ? InternalApiSortDirection.ASC : InternalApiSortDirection.DESC;

  const { data, error } = useQuery({
    queryKey: ['alertNoteTemplates', selectedAlertType, searchTerm, page, size, sortBy, sortDirection],
    queryFn: () =>
      alertNoteTemplateInternalApi.findAlertNoteTemplates({
        page,
        size,
        query: searchTerm || undefined,
        alertType: selectedAlertType === 'ALL' ? undefined : selectedAlertType,
        orderBy: sortBy,
        direction: sortDirection,
      }),
    placeholderData: keepPreviousData,
  });

  const templates = data?.content || [];

  const handleCreate = useCallback(() => {
    setEditingTemplate(null);
    setShowEditor(true);
  }, []);

  const handleEdit = useCallback((template: InternalApiAlertNoteTemplate) => {
    setEditingTemplate(template);
    setShowEditor(true);
  }, []);

  const handleEditorClose = () => {
    setShowEditor(false);
    setEditingTemplate(null);
  };

  const formatAlertType = (alertType: InternalApiAlertType) => {
    return alertType.charAt(0) + alertType.slice(1).toLowerCase();
  };

  const sortingState: SortingState = [
    {
      id: sortByParam === 'NAME' ? 'name' : 'updatedTime',
      desc: sortDirection === InternalApiSortDirection.DESC,
    },
  ];

  const setSorting = useCallback(
    (updaterOrValue: Updater<SortingState>) => {
      const { orderBy = 'UPDATED_TIME', direction = 'DESC' } = getOrderByAndDirection(updaterOrValue, sortingState);

      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev);
        newParams.set('sortBy', orderBy);
        newParams.set('sortDirection', direction);
        return newParams;
      });
      setPage(0);
    },
    [sortingState, setSearchParams]
  );

  const columns = useMemo(
    () => [
      columnHelper.accessor('name', {
        header: 'Name',
        cell: ({ getValue }) => <div className='fw-medium'>{getValue()}</div>,
        enableSorting: true,
      }),
      columnHelper.accessor('alertType', {
        header: 'Alert Type',
        cell: ({ getValue }) => <span className='badge bg-secondary'>{formatAlertType(getValue())}</span>,
      }),
      columnHelper.accessor('content', {
        header: 'Content Preview',
        cell: ({ getValue }) => (
          <div className='text-muted text-truncate' style={{ maxWidth: '300px' }}>
            {getValue()}
          </div>
        ),
      }),
      columnHelper.accessor('createdBy', {
        header: 'Created By',
        cell: ({ getValue }) => <div className='text-muted'>{getValue()?.name || '-'}</div>,
      }),
      columnHelper.accessor('updatedTime', {
        header: 'Last Updated',
        cell: ({ getValue }) => <div className='text-muted'>{dayjs(getValue()).format(FULL_DATE_TIME_FORMAT)}</div>,
        enableSorting: true,
      }),
    ],
    []
  );

  if (error) {
    return (
      <SalvPage title='Alert Note Templates'>
        <div className='alert alert-danger'>Failed to load alert note templates. Please try again.</div>
      </SalvPage>
    );
  }

  return (
    <SalvPage
      title='Alert Note Templates'
      className='alert-note-templates'
      breadcrumb={{
        title: 'Configuration',
        to: getConfigurationUrl(ConfigurationProduct.GENERAL),
      }}
      actions={
        <Has allPermissions={CAN_ADD_ALERT_NOTE_TEMPLATE}>
          <SalvButton label='New template' icon={faPlus} onClick={handleCreate} variant='primary' size={Size.SMALL} />
        </Has>
      }
    >
      <div className='m-b-24' style={{ display: 'flex', gap: '24px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <div>
          <SalvRadioToggleGroup
            defaultValue={selectedAlertType || 'ALL'}
            label='Alert type'
            size={Size.SMALL}
            onChange={(value) => {
              setSearchParams((prev) => {
                const newParams = new URLSearchParams(prev);
                newParams.set('alertType', value);
                return newParams;
              });
            }}
          >
            <SalvRadioToggleButton label='All' value='ALL' />
            <SalvRadioToggleButton label='Monitoring' value={InternalApiAlertType.MONITORING} />
            <SalvRadioToggleButton label='Screening' value={InternalApiAlertType.SCREENING} />
            {isBridgeEnabled && <SalvRadioToggleButton label='Bridge' value={InternalApiAlertType.BRIDGE} />}
            <SalvRadioToggleButton label='Manual' value={InternalApiAlertType.MANUAL} />
          </SalvRadioToggleGroup>
        </div>
        <div style={{ flex: 1 }}>
          <SalvTextField
            ariaLabel='Search templates'
            placeholder='Search by name'
            icon={faMagnifyingGlass}
            value={searchTerm}
            onChange={setSearchTerm}
            size={Size.SMALL}
          />
        </div>
      </div>

      <SalvTable
        columns={columns}
        data={templates}
        striped
        hover={true}
        emptyMessage={
          searchTerm ? 'No templates match your search.' : 'No templates found. Create your first template.'
        }
        rowClick={(row) => handleEdit(row.original)}
        sorting={{
          state: sortingState,
          setSorting: setSorting,
        }}
        pagination={{
          type: SalvTablePaginationType.PAGINATION,
          total: data?.total || 0,
          state: {
            pageIndex: page,
            pageSize: size,
          },
          setPagination: (updater: any) => {
            const newState = typeof updater === 'function' ? updater({ pageIndex: page, pageSize: size }) : updater;
            setPage(newState.pageIndex);
          },
        }}
      />

      {showEditor && (
        <AlertNoteTemplateEditor
          template={editingTemplate}
          defaultAlertType={selectedAlertType !== 'ALL' ? (selectedAlertType as InternalApiAlertType) : undefined}
          onClose={handleEditorClose}
          onSave={() => {
            queryClient.invalidateQueries({ queryKey: ['alertNoteTemplates'] });
            handleEditorClose();
          }}
        />
      )}
    </SalvPage>
  );
};

export default AlertNoteTemplateList;
